import React from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { CreateBetForm } from '../components/bets/CreateBetForm';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { 
    ArrowLeft, 
    Wallet, 
    AlertTriangle,
    Info
} from 'lucide-react';

export function CreateBetPage() {
    const { isConnected, connect, isConnecting, chainName, isMainnet } = useWeb3();

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="create-bet-page">
            {/* Header */}
            <div className="mb-8 animate-fade-in-down">
                <Link to="/bets" className="inline-flex items-center text-white/50 hover:text-[#00FF66] transition-colors mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Bets
                </Link>
                <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white">
                    <span className="text-[#00FF66]">Create</span> a Bet
                </h1>
                <p className="text-white/50 max-w-2xl">
                    Describe your bet, set odds, stake in <span className="text-[#FFD800] font-semibold">ETH, USDC or B4P</span>. Once someone takes the other side,
                    funds are locked until the UMA Oracle resolves the outcome.
                </p>
            </div>

            {/* Connect Wallet Notice */}
            {!isConnected && (
                <Alert className="mb-6 border-[#FFD700]/30 bg-[#FFD700]/10">
                    <AlertTriangle className="w-4 h-4 text-[#FFD700]" />
                    <AlertTitle className="text-[#FFD700]">Wallet Required</AlertTitle>
                    <AlertDescription className="text-white/60 flex items-center justify-between">
                        <span>Connect your wallet to create a bet on Bet4Peer.</span>
                        <Button 
                            onClick={connect}
                            disabled={isConnecting}
                            className="ml-4 bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]"
                            data-testid="connect-wallet-alert-btn"
                        >
                            <Wallet className="w-4 h-4 mr-2" />
                            {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {/* Form */}
            <CreateBetForm />

            {/* Info Cards */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 card-hover animate-fade-in-up delay-1">
                    <h3 className="font-semibold text-lg text-white mb-2">
                        How Odds Work
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                        Odds determine how much the taker must stake. At 2.00x odds, if you stake 1 unit (ETH/USDC/B4P), 
                        the taker stakes 1 unit too. At 3.00x, taker stakes 2 units for your 1 unit.
                    </p>
                </div>
                <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 card-hover animate-fade-in-up delay-2">
                    <h3 className="font-semibold text-lg text-white mb-2">
                        Resolution Process
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                        After the deadline, anyone can propose the outcome via UMA Oracle. 
                        There's a dispute period before the payout is automatically executed.
                    </p>
                </div>
                <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 card-hover animate-fade-in-up delay-3">
                    <h3 className="font-semibold text-lg text-white mb-2">
                        Platform Fee
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                        Bet4Peer takes <span className="text-[#FFD800] font-semibold">2% in B4P</span> or <span className="text-[#FFD800] font-semibold">3% in USDC/ETH</span> from the total pool, deducted from the winner's payout.
                        The loser's stake goes entirely to the winner minus the fee.
                    </p>
                </div>
            </div>
        </div>
    );
}
